import { useState } from 'react';
import { AppDataGrid, type AppColumn } from '../../../components/AppDataGrid';
import { AiCombo } from '../../../components/AiCombo';
import { ExportMenu } from '../../accounting/ExportMenu';
import { useNotify } from '../../../components/notifications';
import { useSaveFlow, useConfirm } from '../../../components/confirm';
import { api } from '../../../api/client';
import { useCities, useSaveCity, useCityStates, errMessage, type CityRow, type StagedCity, type StagedArea } from './city';
import { CityAreasPanel } from './CityAreasPanel';
import { useContentRectStyle } from './useContentRectStyle';
import { useHotkeys, formatCombo } from '../../../components/hotkeys';

interface Props {
  /** Saved State Oid (server mode). Absent ⇒ a new, unsaved state (staged mode). */
  stateId?: string;
  /** Staged cities for a new state (in-memory until the parent state is saved). */
  staged?: StagedCity[];
  onStaged?: (next: StagedCity[]) => void;
  /** Show the in-row editor (the first, pencil column). Default on; the State form turns it off (owner request
   *  2026-10-15) so a city is edited only through its popup (the edit icon). The Country form's State popup keeps it. */
  inlineEdit?: boolean;
}

const newKey = () => (typeof crypto !== 'undefined' && crypto.randomUUID ? crypto.randomUUID() : `k${Date.now()}-${Math.round(Math.random() * 1e6)}`);

/**
 * The City child grid inside the State detail popup — mirrors the legacy
 * `CustomState_DetailView ▸ City` nested list (State → City, City.State FK).
 * Server mode (saved state) commits immediately; staged mode (new state) keeps
 * New/Link/Unlink/Edit usable in memory and commits when the state is saved.
 */
export function StateCitiesPanel({ stateId, staged = [], onStaged, inlineEdit = true }: Props) {
  const staging = !stateId;
  const notify = useNotify();
  const runSave = useSaveFlow();
  const { confirm } = useConfirm();
  const { data: allCities = [], isFetching } = useCities();
  const { data: stateOpts = [] } = useCityStates();   // State dropdown in the City popup
  const saveCity = useSaveCity();

  const mine = stateId ? allCities.filter((c) => c.stateId === stateId) : [];
  const rows: CityRow[] = staging
    ? staged.map((s) => ({ id: s.key, cityName: s.name, stateId: null, state: null }))
    : mine;
  // GoLive's Link lookup lists the FULL City master (every city is a real dbo.City
  // row — a new one is persisted on create), so both modes show allCities.
  const linkSource: CityRow[] = allCities;
  /** The real dbo.City Oid behind a grid row — the row id IS the Oid in server
   *  mode, but a staged KEY on a new state (the Oid lives on the staged item). */
  const realCityId = (rowId: string): string =>
    staging ? (staged.find((s) => s.key === rowId)?.cityId ?? rowId) : rowId;
  /** City Oids ALREADY on this state. GoLive DISABLES the Link picker's edit pencil
   *  for these (they're edited from the grid, not the lookup). */
  const linkedCityIds = new Set(
    (staging ? staged.map((s) => s.cityId).filter(Boolean) as string[] : mine.map((c) => c.id))
      .map((id) => id.toLowerCase()),
  );
  const isLinked = (id: string) => linkedCityIds.has(id.toLowerCase());
  /** Preserve a city's existing State when renaming (the PUT is a full replace). */
  const stateOf = (cityId: string): string | null =>
    allCities.find((c) => c.id.toLowerCase() === cityId.toLowerCase())?.stateId ?? null;

  const [selectedIds, setSelectedIds] = useState<Set<string | number>>(new Set());
  const [dlg, setDlg] = useState<CityRow | null | undefined>(undefined);
  const [name, setName] = useState('');
  const [cityStateId, setCityStateId] = useState('');   // the City popup's State dropdown
  const [cityAreas, setCityAreas] = useState<StagedArea[]>([]);   // Areas staged in the City popup
  const [nameErr, setNameErr] = useState('');
  const [linkOpen, setLinkOpen] = useState(false);
  const [linkSel, setLinkSel] = useState<Set<string | number>>(new Set());
  const [linkSearch, setLinkSearch] = useState('');   // Link-popup search box (LIMS auto-filter)
  const [busy, setBusy] = useState(false);
  const [popupMax, setPopupMax] = useState(false);   // nested popup opens at normal centred size; toggle to maximize
  const popupMaxStyle = useContentRectStyle(popupMax && (dlg !== undefined || linkOpen));
  // Inline row-edit (1st pencil) — City name in place, like the master grids.
  const [inlineId, setInlineId] = useState<string | null>(null);
  const [inlineDraft, setInlineDraft] = useState('');
  const [savingInline, setSavingInline] = useState(false);
  // True while the City edit popup was opened FROM the Link picker, so closing it
  // reopens the (refreshed) picker — GoLive keeps the lookup up.
  const [editFromLink, setEditFromLink] = useState(false);

  const startInline = (r: CityRow) => { setInlineId(r.id); setInlineDraft(r.cityName ?? ''); };
  const cancelInline = () => { setInlineId(null); setInlineDraft(''); };
  const saveInline = async (r: CityRow) => {
    if (!inlineDraft.trim()) { notify.warning("'City' must not to be empty."); return; }
    setSavingInline(true);
    try {
      const v = inlineDraft.trim();
      const cityId = realCityId(r.id);
      if (staging) {
        // Staged cities are real dbo.City rows (persisted on create) — rename the
        // row and keep its current State (the PUT is a full replace).
        await saveCity.mutateAsync({ id: cityId, cityName: v, stateId: stateOf(cityId) });
        onStaged?.(staged.map((s) => (s.key === r.id ? { ...s, name: v } : s)));
      } else {
        await saveCity.mutateAsync({ id: cityId, cityName: v, stateId: r.stateId ?? stateId ?? null });
      }
      cancelInline();
    } catch (e) { notify.error(errMessage(e)); }
    finally { setSavingInline(false); }
  };

  const openNew = () => { setLinkOpen(false); setPopupMax(false); setDlg(null); setName(''); setCityStateId(stateId ?? ''); setCityAreas([]); setNameErr(''); };
  const openEditRow = (r: CityRow) => {
    setLinkOpen(false); setPopupMax(false); setDlg(r); setName(r.cityName ?? ''); setCityStateId(r.stateId ?? stateId ?? ''); setNameErr('');
    // Every listed city is a saved one, so its Area grid is live; nothing is staged for it.
    setCityAreas([]);
  };
  const close = () => {
    setDlg(undefined); setCityAreas([]);
    if (editFromLink) { setEditFromLink(false); setLinkOpen(true); }
  };

  const submit = async () => {
    if (!name.trim()) { setNameErr("'City' must not to be empty."); return; }
    setNameErr('');
    const v = name.trim();
    if (staging) {
      // GoLive persists a new City to dbo.City IMMEDIATELY (only the State link is
      // deferred to the parent save), so it is a real master row visible everywhere
      // — including the Link lookup and other apps on the shared DB. AI used to keep
      // it in memory only, so a city added here never appeared anywhere.
      try {
        if (dlg) {
          const cityId = realCityId(dlg.id);
          await saveCity.mutateAsync({ id: cityId, cityName: v, stateId: cityStateId || stateOf(cityId) });
          onStaged?.(staged.map((s) => (s.key === dlg.id ? { ...s, name: v, areas: [] } : s)));
        } else {
          const res = await saveCity.mutateAsync({ cityName: v, stateId: cityStateId || null });
          const cityId = (res as { data?: { data?: { id?: string } } })?.data?.data?.id;
          // Commit any areas staged on this brand-new city.
          if (cityId && cityAreas.length) {
            for (const a of cityAreas) {
              if (a.areaId) await api.post(`/api/v1/cities/${cityId}/areas/link`, { areaId: a.areaId });
              else await api.post(`/api/v1/cities/${cityId}/areas`, { areaName: a.name });
            }
          }
          // The areas were just committed above, so none are left pending for the parent save
          // (carrying them forward would create them a second time).
          onStaged?.([...staged, { key: newKey(), name: v, cityId, areas: [] }]);
        }
        close();
      } catch (e) { setNameErr(errMessage(e)); }
      return;
    }
    const wasNewCity = !dlg?.id;
    await runSave({
      save: async () => {
        const res = await saveCity.mutateAsync({ id: dlg?.id, cityName: v, stateId: cityStateId || null });
        const newCityId = dlg?.id ?? (res as { data?: { data?: { id?: string } } })?.data?.data?.id;
        // Commit areas staged on this new city (create new / link existing).
        if (wasNewCity && newCityId && cityAreas.length) {
          for (const a of cityAreas) {
            if (a.areaId) await api.post(`/api/v1/cities/${newCityId}/areas/link`, { areaId: a.areaId });
            else await api.post(`/api/v1/cities/${newCityId}/areas`, { areaName: a.name });
          }
        }
      },
      successMessage: 'Saved successfully.', onSaved: () => close(), onError: (e) => notify.error(errMessage(e)),
    });
  };

  const openLink = () => { setDlg(undefined); setPopupMax(false); setLinkSel(new Set()); setLinkSearch(''); setLinkOpen(true); };
  const linkRows = linkSource.filter((c) => { const q = linkSearch.trim().toLowerCase(); return !q || (c.cityName ?? '').toLowerCase().includes(q); });
  const doLink = async () => {
    if (linkSel.size === 0) { notify.warning('Select at least one city to link.'); return; }
    if (staging) {
      // Skip cities already staged onto this state — re-selecting one is a no-op.
      const add = linkSource
        .filter((c) => linkSel.has(c.id) && !isLinked(c.id))
        .map((c) => ({ key: newKey(), name: c.cityName ?? '', cityId: c.id }));
      onStaged?.([...staged, ...add]);
      setLinkOpen(false); setLinkSel(new Set());
      return;
    }
    setBusy(true);
    try {
      for (const id of linkSel) {
        const c = linkSource.find((x) => x.id === String(id));
        if (c) await saveCity.mutateAsync({ id: c.id, cityName: c.cityName ?? '', stateId: stateId! });
      }
      setLinkOpen(false); setLinkSel(new Set());
    } catch (e) { notify.error(errMessage(e)); }
    finally { setBusy(false); }
  };

  const doUnlink = async () => {
    if (selectedIds.size === 0) { notify.warning('Select at least one city to unlink.'); return; }
    if (staging) {
      onStaged?.(staged.filter((s) => !selectedIds.has(s.key)));
      setSelectedIds(new Set());
      return;
    }
    const ok = await confirm({
      title: 'Unlink', tone: 'danger', confirmLabel: 'Unlink',
      message: `Remove ${selectedIds.size} cit${selectedIds.size === 1 ? 'y' : 'ies'} from this state? (The city itself is kept.)`,
    });
    if (!ok) return;
    try {
      for (const id of selectedIds) {
        const c = mine.find((x) => x.id === String(id));
        if (c) await saveCity.mutateAsync({ id: c.id, cityName: c.cityName ?? '', stateId: null });
      }
      setSelectedIds(new Set());
    } catch (e) { notify.error(errMessage(e)); }
  };

  const inlineCol: AppColumn<CityRow>[] = [
    {
      field: 'inlineedit', header: '', sortable: false, filterable: false, width: 44, align: 'center',
      // GoLive shows BOTH pencils on EVERY City row — saved or brand-new state.
      render: (r) => (
        inlineId === r.id ? (
          <span className="pts-rowedit" onClick={(e) => e.stopPropagation()}>
            <button className="pts-icon-btn save" title="Save" disabled={savingInline} onClick={() => void saveInline(r)}><i className="ti ti-device-floppy" /></button>
            <button className="pts-icon-btn cancel" title="Cancel" disabled={savingInline} onClick={cancelInline}><i className="ti ti-arrow-back-up" /></button>
          </span>
        ) : (
          <button className="pts-icon-btn edit" title="Inline edit" onClick={(e) => { e.stopPropagation(); startInline(r); }}><i className="ti ti-pencil" /></button>
        )
      ),
    },
  ];
  const cols: AppColumn<CityRow>[] = [
    ...(inlineEdit ? inlineCol : []),
    {
      field: 'editopen', header: '', sortable: false, filterable: false, width: 44, align: 'center',
      render: (r) => inlineId === r.id ? null : (
        <button className="pts-icon-btn edit" title="Open full edit screen" onClick={(e) => { e.stopPropagation(); openEditRow(r); }}><i className="ti ti-edit" /></button>
      ),
    },
    {
      field: 'cityName', header: 'City', minWidth: 260, text: (r) => r.cityName ?? '', compare: (a, b) => (a.cityName ?? '').localeCompare(b.cityName ?? '', undefined, { sensitivity: 'base' }),
      render: (r) => inlineId === r.id ? (
        <input className="pts-input" autoFocus value={inlineDraft} disabled={savingInline} onClick={(e) => e.stopPropagation()}
          onChange={(e) => setInlineDraft(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') void saveInline(r); if (e.key === 'Escape') cancelInline(); }} />
      ) : (<span>{r.cityName ?? ''}</span>),
    },
  ];
  const linkCols: AppColumn<CityRow>[] = [
    {
      field: 'edit', header: '', sortable: false, filterable: false, width: 44, align: 'center',
      // GoLive DISABLES the edit pencil for a city already in this state's list.
      render: (r) => {
        const linked = isLinked(r.id);
        return (
          <button className="pts-icon-btn edit" disabled={linked}
                  title={linked ? 'Already added to this state — edit it from the City grid' : 'Edit'}
                  style={linked ? { opacity: 0.35, cursor: 'not-allowed' } : undefined}
                  onClick={(e) => { e.stopPropagation(); if (linked) return; setEditFromLink(true); openEditRow(r); }}>
            <i className="ti ti-edit" />
          </button>
        );
      },
    },
    { field: 'cityName', header: 'City', minWidth: 300, text: (r) => r.cityName ?? '', compare: (a, b) => (a.cityName ?? '').localeCompare(b.cityName ?? '', undefined, { sensitivity: 'base' }) },
  ];

  // Toolbar Edit: live while exactly ONE city is ticked; opens that city's popup, the same one the
  // row's open icon opens (owner report 2026-10-09).
  const canEditSelected = selectedIds.size === 1;
  const editSelected = () => {
    const row = rows.find((r) => selectedIds.has(r.id));
    if (canEditSelected && row) openEditRow(row);
  };
  useHotkeys([
    { combo: 'alt+n', label: 'New', group: 'City', order: 1, run: openNew },
    { combo: 'alt+l', label: 'Link', group: 'City', order: 2, run: openLink },
    { combo: 'alt+k', label: 'Unlink', group: 'City', order: 3, disabled: selectedIds.size === 0, run: () => { void doUnlink(); } },
    { combo: 'alt+e', label: 'Edit', group: 'City', order: 4, disabled: !canEditSelected, run: editSelected },
  ]);

  return (
    <div className="acc-subgrid">
      <div className="acc-sub-title">City</div>
      <div className="acc-sub-toolbar">
        <button className="ca-btn" title={`New (${formatCombo('alt+n')})`} onClick={openNew}><i className="ti ti-plus" /> New</button>
        <button className="ca-btn" title={`Link (${formatCombo('alt+l')})`} onClick={openLink}><i className="ti ti-link" /> Link</button>
        <button className="ca-btn" title={`Unlink (${formatCombo('alt+k')})`} onClick={() => void doUnlink()} disabled={selectedIds.size === 0}><i className="ti ti-unlink" /> Unlink</button>
        <button className="ca-btn" title={`Edit the ticked city (${formatCombo('alt+e')})`} onClick={editSelected} disabled={!canEditSelected}><i className="ti ti-edit" /> Edit</button>
        <ExportMenu triggerClassName="ca-btn" filename="City" headers={['City']} rows={rows.map((c) => [c.cityName ?? ''])} />
      </div>

      <AppDataGrid<CityRow>
        rows={rows} columns={cols} getRowId={(r) => r.id}
        selectable selectedIds={selectedIds} onSelectionChange={setSelectedIds} selectedRowId={inlineId ?? dlg?.id ?? null}
        initialSort={{ key: 'cityName', dir: 'asc' }} loading={!staging && isFetching} emptyText="No data to display"
        defaultPageSize={20}
      />

      {dlg !== undefined && (
        <div className="acc-overlay" onMouseDown={(e) => { e.stopPropagation(); close(); }}>
          <div className={`acc-dialog acc-dialog-md acc-dialog--accent${popupMax ? ' acc-dialog-max' : ''}`} style={popupMaxStyle} onMouseDown={(e) => e.stopPropagation()}>
            <div className="acc-dialog-hdr">
              <span>{dlg ? `${dlg.cityName ?? ''} - City` : 'City'}</span>
              <span className="acc-dialog-acts">
                <i className={`ti ${popupMax ? 'ti-minimize' : 'ti-maximize'} acc-dialog-x`} title={popupMax ? 'Restore' : 'Maximize'} onClick={() => setPopupMax((m) => !m)} />
                <i className="ti ti-x acc-dialog-x" onClick={close} />
              </span>
            </div>
            <div className="acc-dialog-body">
              <div className="acc-field-h">
                <label className="acc-label acc-label-req">City<span className="req">*</span></label>
                <div className="acc-fh-control">
                  <input className="acc-input" autoFocus value={name} onChange={(e) => { setName(e.target.value); if (nameErr) setNameErr(''); }}
                    onKeyDown={(e) => { if (e.key === 'Enter') void submit(); }} />
                  {nameErr && <div className="acc-err">{nameErr}</div>}
                </div>
              </div>
              <div className="acc-field-h">
                <label className="acc-label">State</label>
                <div className="acc-fh-control">
                  {/* State lookup: dropdown + erase (clear) icon — LIMS CustomState lookup editor. */}
                  <AiCombo value={cityStateId} options={stateOpts.map((s) => ({ value: s.id, label: (s.longName ?? '').trim() || s.shortName }))}
                           onChange={(v) => setCityStateId(v ?? '')} />
                </div>
              </div>
              {/* Area child grid — City → Area. A saved city commits immediately; a new
                  city stages areas in memory (committed when the city is saved). */}
              {/* A city listed here is always a real, saved city — even on a not-yet-saved state, where only
                  its link to the state is pending — so its Area grid reads and writes that city's own areas.
                  Only a brand-new city (no id yet) stages its areas until it is created. */}
              <CityAreasPanel cityId={dlg?.id ? realCityId(dlg.id) : undefined} staged={cityAreas} onStaged={setCityAreas} />
            </div>
            <div className="acc-dialog-ftr">
              <button className="ca-btn ca-btn-solid" onClick={() => void submit()} disabled={saveCity.isPending}><span className="tb-i">💾</span> OK</button>
              <button className="ca-btn" onClick={close}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
            </div>
          </div>
        </div>
      )}

      {linkOpen && (
        <div className="acc-overlay" onMouseDown={(e) => { e.stopPropagation(); setLinkOpen(false); }}>
          <div className={`acc-dialog acc-dialog-pick acc-dialog--accent${popupMax ? ' acc-dialog-max' : ''}`} style={popupMaxStyle} onMouseDown={(e) => e.stopPropagation()}>
            {/* Every popup carries BOTH maximize/restore and close (GoLive chrome). */}
            <div className="acc-dialog-hdr">
              <span>City</span>
              <span className="acc-dialog-acts">
                <i className={`ti ${popupMax ? 'ti-minimize' : 'ti-maximize'} acc-dialog-x`} title={popupMax ? 'Restore' : 'Maximize'} onClick={() => setPopupMax((m) => !m)} />
                <i className="ti ti-x acc-dialog-x" onClick={() => setLinkOpen(false)} />
              </span>
            </div>
            <div className="acc-dialog-body">
              <div className="acc-pick-search"><div className="acc-search"><i className="ti ti-search" />
                <input placeholder="Search…" autoFocus value={linkSearch} onChange={(e) => setLinkSearch(e.target.value)} />
                {linkSearch && <i className="ti ti-x acc-search-x" onClick={() => setLinkSearch('')} />}
              </div></div>
              <AppDataGrid<CityRow>
                rows={linkRows} columns={linkCols} getRowId={(r) => r.id}
                selectable selectedIds={linkSel} onSelectionChange={setLinkSel}
                initialSort={{ key: 'cityName', dir: 'asc' }} emptyText="No cities available to link."
                defaultPageSize={20}
              />
            </div>
            <div className="acc-dialog-ftr">
              <button className="ca-btn ca-btn-solid" disabled={busy || linkSel.size === 0} onClick={() => void doLink()}>OK</button>
              <button className="ca-btn" disabled={busy} onClick={() => setLinkOpen(false)}><span className="tb-i tb-i-grey">↩︎</span> Cancel</button>
            </div>
          </div>
        </div>
      )}

    </div>
  );
}
